import React, { useState, useEffect } from "react";
import { FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";

// ─── Zone Themes ───────────────────────────────────────────────────────────────
const ZONE_THEMES: Record<string, { bg: string; text: string; accent: string }> = {
  "Green Zone":  { bg: "linear-gradient(135deg, #22c55e, #16a34a)", text: "#fff", accent: "#bbf7d0" },
  "Blue Zone":   { bg: "linear-gradient(135deg, #3b82f6, #2563eb)", text: "#fff", accent: "#bfdbfe" },
  "Yellow Zone": { bg: "linear-gradient(135deg, #eab308, #ca8a04)", text: "#fff", accent: "#fef08a" },
  "Orange Zone": { bg: "linear-gradient(135deg, #f97316, #ea580c)", text: "#fff", accent: "#fed7aa" },
  "Brown Zone":  { bg: "linear-gradient(135deg, #d97706, #b45309)", text: "#fff", accent: "#fde68a" },
  "Red Zone":    { bg: "linear-gradient(135deg, #ef4444, #dc2626)", text: "#fff", accent: "#fecaca" },
  "Light Red":   { bg: "linear-gradient(135deg, #ef4444, #dc2626)", text: "#fff", accent: "#fecaca" },
  "Dark Red":    { bg: "linear-gradient(135deg, #b91c1c, #991b1b)", text: "#fff", accent: "#fca5a5" },
};
const defaultTheme = { bg: "linear-gradient(135deg, #6b7280, #4b5563)", text: "#fff", accent: "#d1d5db" };

// ─── Score Bar ─────────────────────────────────────────────────────────────────
const ScoreBar: React.FC<{ value: number; max?: number; color: string; label: string }> = ({
  value,
  max = 7,
  color,
  label,
}) => {
  const pct = Math.round((Math.min(value, max) / max) * 100);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{label}</span>
        <span className="text-sm font-black text-gray-700">
          {value}<span className="text-gray-300 font-semibold text-xs">/{max}</span>
        </span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
};

// ─── Entry Interface ───────────────────────────────────────────────────────────
export interface Entry {
  id: number;
  date: string;
  time: string;
  feelingDescription?: string;
  impact: number;
  joyfulness: number;
  calculatedEmotion?: string;
  calculatedZone?: string;
  category?: { name: string };
  subCategory?: { name: string };
}

interface EntryDetailModalProps {
  entries: Entry[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
}

// ─── Modal ─────────────────────────────────────────────────────────────────────
const EntryDetailModal: React.FC<EntryDetailModalProps> = ({
  entries,
  initialIndex = 0,
  isOpen,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setTimeout(() => setIsRendered(true), 10);
    } else {
      setIsRendered(false);
    }
  }, [isOpen, initialIndex]);

  // Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    if (isOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen && !isRendered) return null;
  if (!entries || entries.length === 0) return null;

  const entry = entries[currentIndex];
  if (!entry) return null;

  // Resolve theme: try exact key, then with " Zone" appended, then without " Zone"
  // This covers all formats the backend might send:
  // "Blue" | "Blue Zone" | "Red Zone" | "Light Red" | "Dark Red" etc.
  const resolveZoneTheme = (raw: string) => {
    if (ZONE_THEMES[raw]) return ZONE_THEMES[raw];
    const withZone = raw.includes("Zone") ? raw : `${raw} Zone`;
    if (ZONE_THEMES[withZone]) return ZONE_THEMES[withZone];
    const withoutZone = raw.replace(" Zone", "").trim();
    if (ZONE_THEMES[withoutZone]) return ZONE_THEMES[withoutZone];
    return defaultTheme;
  };
  const theme = entry.calculatedZone ? resolveZoneTheme(entry.calculatedZone) : defaultTheme;

  const dateObj = new Date(`${entry.date}T${entry.time || "00:00:00"}`);
  const dateStr = dateObj.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  const timeStr = entry.time ? entry.time.slice(0, 5) : "—";
  const catName = entry.category?.name || "Uncategorized";
  const subCatName = entry.subCategory?.name;

  // Header title = emotion name (always clear and prominent)
  const emotionTitle = entry.calculatedEmotion || "Log Entry";

  const handlePrev = () => setCurrentIndex((c) => Math.max(0, c - 1));
  const handleNext = () => setCurrentIndex((c) => Math.min(entries.length - 1, c + 1));

  // Friendly zone display (strip "Zone" for badge readability)
  const zoneBadgeLabel = entry.calculatedZone?.replace(" Zone", "") ?? null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${
        isRendered ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm" onClick={onClose} />

      {/* Card */}
      <div
        className={`relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden transition-all duration-300 transform ${
          isRendered ? "scale-100 translate-y-0" : "scale-95 translate-y-4"
        }`}
      >
        {/* ── Header ────────────────────────────────────────────────────────── */}
        <div className="relative p-6" style={{ background: theme.bg, color: theme.text }}>
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <FiX size={18} />
          </button>

          {/* Label row: "Entry Detail" + log counter */}
          <div className="flex items-center gap-2 mb-1">
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-70">
              Entry Detail
            </p>
            {entries.length > 1 && (
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: "rgba(0,0,0,0.18)", color: theme.text }}
              >
                {currentIndex + 1} of {entries.length} logs
              </span>
            )}
          </div>

          {/* Emotion title */}
          <h2 className="text-2xl font-black capitalize leading-tight mb-2">
            {emotionTitle}
          </h2>

          {/* Meta row: date · time · category */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium opacity-90 mb-3">
            <span>{dateStr}</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {timeStr}
            </span>
            <span>•</span>
            <span>{catName}</span>
            {subCatName && (
              <>
                <span className="opacity-50">›</span>
                <span className="opacity-80">{subCatName}</span>
              </>
            )}
          </div>

          {/* Zone badge */}
          {zoneBadgeLabel && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full"
              style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(4px)" }}>
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: theme.accent }}
              />
              <span className="text-xs font-bold tracking-wide">{zoneBadgeLabel}</span>
            </div>
          )}

          {/* Multi-log navigation */}
          {entries.length > 1 && (
            <div className="mt-4 flex items-center justify-center gap-4 py-1.5 px-4 bg-black/10 rounded-full w-fit mx-auto backdrop-blur-md">
              <button
                onClick={handlePrev}
                disabled={currentIndex === 0}
                className="p-1 rounded-full hover:bg-white/20 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              >
                <FiChevronLeft size={18} />
              </button>
              <span className="text-xs font-bold tracking-wider">
                {currentIndex + 1} / {entries.length}
              </span>
              <button
                onClick={handleNext}
                disabled={currentIndex === entries.length - 1}
                className="p-1 rounded-full hover:bg-white/20 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              >
                <FiChevronRight size={18} />
              </button>
            </div>
          )}
        </div>

        {/* ── Body ──────────────────────────────────────────────────────────── */}
        <div className="p-6 pb-8 space-y-5">

          {/* Sub-category chip — shown prominently if exists */}
          {subCatName && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Sub-area
              </span>
              <span
                className="text-xs font-bold px-2.5 py-1 rounded-full border"
                style={{ background: "#f8fafc", borderColor: "#e2e8f0", color: "#475569" }}
              >
                {subCatName}
              </span>
            </div>
          )}

          {/* Score bars: Impact + Joyfulness */}
          <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 space-y-4">
            <ScoreBar
              value={entry.impact}
              max={7}
              color="linear-gradient(90deg, #f97316, #ea580c)"
              label="Impact"
            />
            <ScoreBar
              value={entry.joyfulness}
              max={7}
              color="linear-gradient(90deg, #1ecab8, #0d9488)"
              label="Joyfulness (Pleasant)"
            />
          </div>

          {/* Quick stats row: raw numbers + zone */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gray-50 rounded-xl p-2.5 flex flex-col items-center justify-center text-center border border-gray-100">
              <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">Impact</span>
              <span className="text-lg font-black text-gray-800">{entry.impact}</span>
              <span className="text-[9px] text-gray-300">/ 7</span>
            </div>
            <div className="bg-gray-50 rounded-xl p-2.5 flex flex-col items-center justify-center text-center border border-gray-100">
              <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">Pleasant</span>
              <span className="text-lg font-black text-gray-800">{entry.joyfulness}</span>
              <span className="text-[9px] text-gray-300">/ 7</span>
            </div>
            <div
              className="rounded-xl p-2.5 flex flex-col items-center justify-center text-center border"
              style={entry.calculatedZone
                ? { background: theme.bg, borderColor: "transparent" }
                : { background: "#f9fafb", borderColor: "#f3f4f6" }
              }
            >
              <span
                className="text-[9px] font-bold uppercase tracking-widest mb-1"
                style={{ color: entry.calculatedZone ? "rgba(255,255,255,0.75)" : "#9ca3af" }}
              >
                Zone
              </span>
              <span
                className="text-[10px] font-black leading-tight text-center"
                style={{ color: entry.calculatedZone ? "#fff" : "#6b7280" }}
              >
                {zoneBadgeLabel ?? "—"}
              </span>
            </div>
          </div>

          {/* Personal Journal */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Personal Journal</h3>
            </div>
            {entry.feelingDescription ? (
              <div className="bg-indigo-50/50 border border-indigo-100/50 p-4 rounded-2xl text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {entry.feelingDescription}
              </div>
            ) : (
              <div className="bg-gray-50 p-4 rounded-2xl text-sm text-gray-400 italic text-center">
                No journal notes were recorded for this entry.
              </div>
            )}
          </div>

          {/* Smart Insight (optional) */}
          {(entry as any).smartInsight && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Smart Insight</h3>
              </div>
              <div className="bg-amber-50/50 border border-amber-100/50 p-4 rounded-2xl text-sm text-gray-700 leading-relaxed">
                {(entry as any).smartInsight}
              </div>
            </div>
          )}

          {/* Close */}
          <button
            onClick={onClose}
            className="w-full py-3.5 rounded-2xl bg-gray-900 text-white text-sm font-bold shadow-lg shadow-gray-900/20 hover:bg-gray-800 hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-[0.98]"
          >
            CLOSE REFLECTION
          </button>
        </div>
      </div>
    </div>
  );
};

export default EntryDetailModal;
